import React, { useEffect, useRef, useState } from 'react';

/**
 * Filter row used by every list page. All pieces are optional — pass only
 * the props the page needs and the component renders the matching control.
 *
 *   <FilterBar
 *     searchValue={...}            onSearchChange={(v) => ...}     searchPlaceholder="..."
 *     dateValue={...}              onDateChange={(v) => ...}       dateOptions={[{label, value}]}
 *     statusValue={...}            onStatusChange={(v) => ...}     statusOptions={[{label, value}]}
 *     mediatorValue={...}          onMediatorChange={(v) => ...}   mediators={[{userId, name}]}
 *   />
 *
 * Layout matches the Cases page: search left (280px), filters right.
 */
export default function FilterBar({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search...',
  dateValue,
  onDateChange,
  dateOptions,
  dateDefaultValue,
  customStart,
  customEnd,
  onCustomDateChange,
  // Optional "date basis" pill — when `dateFieldOptions` is provided, a
  // segmented control renders LEFT of the date picker so the user can
  // choose which column the date range applies to (e.g. mediation_date
  // vs created_at). Pages that don't pass this prop get the old layout.
  dateFieldValue,
  onDateFieldChange,
  dateFieldOptions,
  statusValue,
  onStatusChange,
  statusOptions,
  mediatorValue,
  onMediatorChange,
  mediators,
  // Optional class merged onto the outer wrapper. Pages that already
  // own their row spacing (e.g. MediatorDetail) pass `!mb-0` to drop
  // the default `mb-6` so the row aligns with sibling controls.
  className = '',
}) {
  return (
    <div className={`flex items-center justify-between mb-6 flex-wrap gap-3 ${className}`}>
      {onSearchChange && (
        <div className="relative" style={{ width: 280 }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
            <path d="M6.417 11.083A4.667 4.667 0 106.417 1.75a4.667 4.667 0 000 9.333zM12.25 12.25l-2.537-2.537" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full h-8 pl-9 pr-3 rounded-lg border border-[#e5e5e5] bg-white text-[13px] text-[#0e1e20] placeholder-[#9ca3af] outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
            style={{ fontFamily: 'Inter' }}
          />
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {dateFieldOptions && dateFieldOptions.length >= 2 && (
          <DateBasisFilter
            value={dateFieldValue}
            options={dateFieldOptions}
            onChange={onDateFieldChange}
          />
        )}
        {dateOptions && (
          <DateFilter
            value={dateValue}
            options={dateOptions}
            defaultValue={dateDefaultValue}
            onChange={onDateChange}
            customStart={customStart}
            customEnd={customEnd}
            onCustomChange={onCustomDateChange}
          />
        )}
        {statusOptions && (
          <StatusFilter value={statusValue} options={statusOptions} onChange={onStatusChange} />
        )}
        {mediators && (
          <MediatorFilter value={mediatorValue} mediators={mediators} onChange={onMediatorChange} />
        )}
      </div>
    </div>
  );
}

/* ── Date filter ─────────────────────────────────────────────────────────
 *
 * Two-pane popover:
 *   • Left  — preset list (All time / Upcoming / This week / … / Custom)
 *             plus a Reset button at the bottom.
 *   • Right — month-view calendar with prev/next navigation. Clicking a
 *             day picks a custom range: first click sets the start, second
 *             click sets the end (auto-swapped if user picks the earlier
 *             date second). Picking on the calendar always switches the
 *             active preset to 'custom'.
 *
 * Parent reads `value` for the preset; when value === 'custom' it also
 * reads `customStart` / `customEnd` (ISO yyyy-mm-dd).
 */
function DateFilter({
  value,
  options,
  defaultValue,
  onChange,
  customStart,
  customEnd,
  onCustomChange,
}) {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(() => setOpen(false));

  const today = new Date();
  // Calendar's currently visible month.
  const initial = parseIso(customStart) || today;
  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  // Re-sync view when the parent's customStart changes externally.
  useEffect(() => {
    const d = parseIso(customStart);
    if (d) { setViewYear(d.getFullYear()); setViewMonth(d.getMonth()); }
  }, [customStart]);

  const isCustom = value === 'custom' && (customStart || customEnd);
  const selected = options.find((o) => o.value === value) || options.find((o) => o.value === defaultValue) || options[0];
  const isNonDefault = value && value !== defaultValue;
  const buttonLabel = isCustom
    ? `${customStart || '…'} → ${customEnd || '…'}`
    : (selected ? selected.label : 'Date');

  // The preset list mirrors the screenshot: include a synthetic 'custom'
  // entry so users can switch back to picking dates after choosing a
  // preset. Pages can keep using DEFAULT_DATE_PRESETS — we tack 'Custom'
  // on at render time instead of forcing every caller to.
  const renderedOptions = options.some((o) => o.value === 'custom')
    ? options
    : [...options, { label: 'Custom', value: 'custom' }];

  const pickPreset = (val) => {
    if (val === 'custom') {
      // Don't clear the dates; just switch the value so the calendar
      // becomes the source of truth.
      onChange('custom');
      return;
    }
    if (onCustomChange) onCustomChange('', '');
    onChange(val);
    setOpen(false);
  };

  const reset = () => {
    if (onCustomChange) onCustomChange('', '');
    onChange(defaultValue || (options[0] && options[0].value) || 'all');
  };

  const handleDayClick = (date) => {
    const iso = isoDate(date);
    // If nothing picked yet, or both ends already set, start a new range.
    if (!customStart || (customStart && customEnd)) {
      if (onCustomChange) onCustomChange(iso, '');
      onChange('custom');
      return;
    }
    // We have a start, no end. Set the end (swap if user picked earlier).
    if (iso < customStart) {
      if (onCustomChange) onCustomChange(iso, customStart);
    } else {
      if (onCustomChange) onCustomChange(customStart, iso);
    }
    onChange('custom');
  };

  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const grid = monthGrid(viewYear, viewMonth);
  const prevMonth = () => {
    const m = viewMonth - 1;
    if (m < 0) { setViewMonth(11); setViewYear(viewYear - 1); } else setViewMonth(m);
  };
  const nextMonth = () => {
    const m = viewMonth + 1;
    if (m > 11) { setViewMonth(0); setViewYear(viewYear + 1); } else setViewMonth(m);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border ${isNonDefault || isCustom ? 'border-[#115e59] bg-[#f0fdfa]' : 'border-[#e5e5e5]'}`}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M4.667 1.167V2.333" stroke="#5b5a64" strokeWidth="1.167" strokeLinecap="round" />
          <path d="M9.333 1.167V2.333" stroke="#5b5a64" strokeWidth="1.167" strokeLinecap="round" />
          <rect x="1.75" y="2.333" width="10.5" height="10.5" rx="1" stroke="#5b5a64" strokeWidth="1.167" />
          <path d="M1.75 5.833h10.5" stroke="#5b5a64" strokeWidth="1.167" />
        </svg>
        <span className="text-[13px] font-medium text-[#0e1e20] truncate max-w-[200px]" style={{ fontFamily: 'Inter' }}>
          {buttonLabel}
        </span>
        <ChevronDownSmall />
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1 bg-white border border-[#e5e5e5] rounded-lg shadow-lg z-50 flex"
          style={{ width: 580 }}
          role="dialog"
          aria-label="Date filter"
        >
          {/* Left pane — presets + reset */}
          <div className="flex flex-col" style={{ width: 240, borderRight: '1px solid #e5e5e5' }}>
            <div className="flex flex-col p-2 gap-1 flex-1 overflow-auto" style={{ maxHeight: 360 }}>
              {renderedOptions.map((opt) => {
                const active = (opt.value === 'custom' ? isCustom : (value === opt.value && !isCustom));
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => pickPreset(opt.value)}
                    className={`w-full text-left px-3 py-2 rounded-md text-[14px] hover:bg-[#f5f5f5] ${active ? 'bg-[#eef2f7] text-[#0e1e20] font-medium' : 'text-[#0e1e20]'}`}
                    style={{ fontFamily: 'Inter' }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div className="border-t border-[#e5e5e5] p-2">
              <button
                type="button"
                onClick={reset}
                className="w-full text-center px-3 py-2 rounded-md text-[14px] text-[#0e1e20] hover:bg-[#f5f5f5]"
                style={{ fontFamily: 'Inter' }}
              >
                Reset
              </button>
            </div>
          </div>
          {/* Right pane — month calendar */}
          <div className="flex-1 p-3" style={{ width: 340 }}>
            <div className="flex items-center justify-between mb-2 px-1">
              <button
                type="button"
                onClick={prevMonth}
                aria-label="Previous month"
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-[#f5f5f5]"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M8.75 3.5L5.25 7L8.75 10.5" stroke="#0e1e20" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <span className="text-[14px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter' }}>{monthLabel}</span>
              <button
                type="button"
                onClick={nextMonth}
                aria-label="Next month"
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-[#f5f5f5]"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="#0e1e20" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <div className="grid grid-cols-7 gap-y-1 px-1 mb-1">
              {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sat', 'Su'].map((d) => (
                <span key={d} className="text-[11px] font-medium text-[#5b5a64] text-center" style={{ fontFamily: 'Inter' }}>{d}</span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-1 px-1">
              {grid.map((cell) => {
                const cellIso = isoDate(cell.date);
                const isStart = customStart === cellIso;
                const isEnd = customEnd === cellIso;
                const inRange = customStart && customEnd && cellIso > customStart && cellIso < customEnd;
                const inMonth = cell.inMonth;
                return (
                  <button
                    key={cellIso}
                    type="button"
                    onClick={() => handleDayClick(cell.date)}
                    className={`h-9 rounded-md flex items-center justify-center text-[13px] ${
                      isStart || isEnd
                        ? 'border-2 border-[#0e1e20] font-semibold text-[#0e1e20]'
                        : inRange
                        ? 'bg-[#eef2f7] text-[#0e1e20]'
                        : inMonth
                        ? 'text-[#0e1e20] hover:bg-[#f5f5f5]'
                        : 'text-[#bcbcbc] hover:bg-[#f5f5f5]'
                    }`}
                    style={{ fontFamily: 'Inter' }}
                  >
                    {cell.date.getDate()}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* Build the 6×7 day grid for a given (year, month). Includes leading
 * days from the previous month and trailing days from the next month so
 * the layout is always 6 rows. Week starts Monday. */
function monthGrid(year, month) {
  const firstOfMonth = new Date(year, month, 1);
  // Day-of-week with Monday=0, Sunday=6
  const lead = (firstOfMonth.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - lead);
  const out = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    out.push({ date: d, inMonth: d.getMonth() === month });
  }
  return out;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseIso(s) {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/* Helper exposed for pages so they can convert `dateFilter` → ISO range
 * the API expects. Matches the algorithm used inline in Cases.jsx today.
 * Returns { startDate, endDate } where empty strings mean unbounded. */
export function dateRangeForFilter(filter, customStart, customEnd) {
  if (filter === 'custom') {
    return { startDate: customStart || '', endDate: customEnd || '' };
  }
  // Anchor presets to the Pacific business "today", NOT the browser's local
  // clock. For a user east of Pacific late at night the browser day can already
  // be tomorrow Pacific — so "today"/"this month" would land a day/month off
  // versus Scale. We build a Date whose Y-M-D is the Pacific calendar date
  // (midnight local); all preset math below only reads Y-M-D, so the local
  // time-of-day is irrelevant.
  const _pacParts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const _pac = (t) => Number(_pacParts.find((p) => p.type === t).value);
  const now = new Date(_pac('year'), _pac('month') - 1, _pac('day'));
  // Format the LOCAL calendar date (Y-M-D) — NOT toISOString(), which converts
  // to UTC and shifts month/week boundaries by the browser's offset, bleeding an
  // adjacent-day case into the range (e.g. "last month" pulling in a Jun-1 or
  // Apr-30 mediation_date). Pure local Y-M-D keeps the preset on the intended
  // calendar dates in every timezone — matching Scale Portal's date filters.
  const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return iso(x); };
  const endOfDay = (d) => { const x = new Date(d); x.setHours(23,59,59,999); return iso(x); };
  const monday = (d) => { const x = new Date(d); const day = x.getDay(); x.setDate(x.getDate() - (day === 0 ? 6 : day - 1)); return x; };
  const sunday = (d) => { const x = monday(d); x.setDate(x.getDate() + 6); return x; };
  switch (filter) {
    // Scale parity: "Upcoming" = from TODAY onward, unbounded end (Scale caps
    // at the last mediation date, which is effectively all future cases). CM
    // previously started at the 1st of the current month (pulling in past
    // early-month cases) and capped at +120 days (dropping cases beyond ~4mo).
    case 'upcoming': return { startDate: startOfDay(now), endDate: '' };
    case 'today': return { startDate: startOfDay(now), endDate: endOfDay(now) };
    case 'thisweek': return { startDate: startOfDay(monday(now)), endDate: endOfDay(sunday(now)) };
    case 'nextweek': { const n = new Date(now); n.setDate(now.getDate() + 7); return { startDate: startOfDay(monday(n)), endDate: endOfDay(sunday(n)) }; }
    case 'lastweek': { const l = new Date(now); l.setDate(now.getDate() - 7); return { startDate: startOfDay(monday(l)), endDate: endOfDay(sunday(l)) }; }
    case 'thismonth': return { startDate: `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`, endDate: endOfDay(new Date(now.getFullYear(), now.getMonth()+1, 0)) };
    case 'nextmonth': { const m = new Date(now.getFullYear(), now.getMonth()+1, 1); return { startDate: startOfDay(m), endDate: endOfDay(new Date(m.getFullYear(), m.getMonth()+1, 0)) }; }
    case 'lastmonth': { const m = new Date(now.getFullYear(), now.getMonth()-1, 1); return { startDate: startOfDay(m), endDate: endOfDay(new Date(m.getFullYear(), m.getMonth()+1, 0)) }; }
    case 'last30': { const d = new Date(now); d.setDate(now.getDate() - 30); return { startDate: startOfDay(d), endDate: endOfDay(now) }; }
    case 'last90': { const d = new Date(now); d.setDate(now.getDate() - 90); return { startDate: startOfDay(d), endDate: endOfDay(now) }; }
    default: return { startDate: '', endDate: '' };
  }
}

/* Default preset list — matches the canonical filter design (the
 * synthetic 'Custom' entry is appended automatically by DateFilter, so
 * pages don't need to include it). */
export const DEFAULT_DATE_PRESETS = [
  { label: 'All time', value: 'all' },
  { label: 'Upcoming', value: 'upcoming' },
  { label: 'This week', value: 'thisweek' },
  { label: 'Next week', value: 'nextweek' },
  { label: 'This month', value: 'thismonth' },
  { label: 'Next month', value: 'nextmonth' },
  { label: 'Last 90 days', value: 'last90' },
];

/* ── Status filter ── */
function StatusFilter({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useOutsideClose(() => setOpen(false));
  const selected = options.find((o) => o.value === value);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border ${value ? 'border-[#115e59] bg-[#f0fdfa]' : 'border-[#e5e5e5]'}`}
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.333 3.5h9.334M4.083 7h5.834M5.833 10.5h2.334" stroke="#5b5a64" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-[13px] font-medium text-[#0e1e20]" style={{ fontFamily: 'Inter' }}>
          Status: {selected ? selected.label : 'All'}
        </span>
        <ChevronDownSmall />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 w-[200px] bg-white border border-[#e5e5e5] rounded-lg shadow-lg z-50">
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#f5f5f5] ${!value ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#333]'}`}
            style={{ fontFamily: 'Inter' }}
          >
            All
          </button>
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#f5f5f5] ${value === opt.value ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#333]'}`}
              style={{ fontFamily: 'Inter' }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Mediator filter ── */
function MediatorFilter({ value, mediators, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);
  const ref = useOutsideClose(() => { setOpen(false); setQuery(''); });
  const selected = mediators.find((m) => m.userId === value);
  // Auto-focus the search input when the dropdown opens.
  useEffect(() => {
    if (open && searchRef.current) {
      const t = setTimeout(() => searchRef.current && searchRef.current.focus(), 0);
      return () => clearTimeout(t);
    }
  }, [open]);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? mediators.filter((m) => (m.name || '').toLowerCase().includes(q))
    : mediators;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 h-8 px-3 rounded-lg border ${value ? 'border-[#115e59] bg-[#f0fdfa]' : 'border-[#e5e5e5]'}`}
      >
        <span className="text-[13px] font-medium text-[#0e1e20] max-w-[150px] truncate" style={{ fontFamily: 'Inter' }}>
          {selected ? selected.name : 'Mediator'}
        </span>
        <ChevronDownSmall />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 w-[240px] bg-white border border-[#e5e5e5] rounded-lg shadow-lg z-50 flex flex-col" style={{ maxHeight: 320 }}>
          <div className="p-2 border-b border-[#e5e5e5]">
            <div className="relative">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                <path d="M6.417 11.083A4.667 4.667 0 106.417 1.75a4.667 4.667 0 000 9.333zM12.25 12.25l-2.537-2.537" stroke="#9ca3af" strokeWidth="1.167" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                placeholder="Search mediators..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full h-8 pl-8 pr-2 rounded-md border border-[#e5e5e5] bg-white text-[13px] text-[#0e1e20] placeholder-[#9ca3af] outline-none focus:border-[#115e59] focus:ring-1 focus:ring-[#115e59]/20"
                style={{ fontFamily: 'Inter' }}
              />
            </div>
          </div>
          <div className="overflow-auto flex-1">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
              className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#f5f5f5] ${!value ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#333]'}`}
              style={{ fontFamily: 'Inter' }}
            >
              All Mediators
            </button>
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-[12px] text-[#9ca3af]" style={{ fontFamily: 'Inter' }}>
                No mediators found
              </div>
            )}
            {filtered.map((m) => (
              <button
                key={m.userId || m.id}
                type="button"
                onClick={() => { onChange(m.userId); setOpen(false); setQuery(''); }}
                className={`w-full text-left px-3 py-2 text-[13px] hover:bg-[#f5f5f5] ${value === m.userId ? 'bg-[#e8fdf5] text-[#115e59] font-medium' : 'text-[#333]'}`}
                style={{ fontFamily: 'Inter' }}
              >
                {m.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Date Basis radio group (e.g. Mediation Date / Booking Date) ─────
 *
 * Inline radio group. The page owns the selected value and decides what
 * each option means in BE-query terms (forwarded as the `dateField`
 * query param). The component is intentionally generic so other pages
 * could reuse it for any "which date column does the range apply to"
 * decision.
 *
 * Uses a name-scoped via React.useId so two FilterBars on the same
 * page don't share radio state.
 */
function DateBasisFilter({ value, options, onChange }) {
  const groupName = React.useId();
  return (
    <div
      className="inline-flex items-center gap-3 h-8 px-3 rounded-lg border border-[#e5e5e5] bg-white"
      role="radiogroup"
      aria-label="Date field"
    >
      {options.map((opt) => {
        const active = opt.value === value;
        const inputId = `${groupName}-${opt.value}`;
        return (
          <label
            key={opt.value}
            htmlFor={inputId}
            className="inline-flex items-center gap-1.5 cursor-pointer select-none"
            style={{ fontFamily: 'Inter' }}
          >
            <input
              id={inputId}
              type="radio"
              name={groupName}
              value={opt.value}
              checked={active}
              onChange={() => onChange?.(opt.value)}
              className="cursor-pointer accent-[#115e59]"
              style={{ width: 14, height: 14 }}
            />
            <span className={`text-[12px] ${active ? 'font-semibold text-[#0e1e20]' : 'font-medium text-[#5b5a64]'}`}>
              {opt.label}
            </span>
          </label>
        );
      })}
    </div>
  );
}

/* ── Helpers ── */
function ChevronDownSmall() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="#0e1e20" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function useOutsideClose(handler) {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) handler();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [handler]);
  return ref;
}
